import { useMemo, useState } from 'react';

interface Props {
  from: string | null;
  to: string | null;
  onChange: (range: { from: string; to: string }) => void;
  /** Earliest selectable day, normally today at the site. */
  minDate: string;
  /** Latest selectable day, or null when the office has no limit. */
  maxDate: string | null;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const parse = (s: string) => new Date(`${s}T00:00:00Z`);

/** Monday-first weekday index, 0-6. */
const mondayIndex = (d: Date) => (d.getUTCDay() + 6) % 7;

/**
 * The days to draw for a month, padded to whole weeks. Padding days
 * belong to the neighbouring months and are drawn faintly rather than
 * left blank, so the grid keeps its shape.
 */
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - mondayIndex(first));

  const days: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    days.push(d);
  }
  return days;
}

/**
 * Picking a stretch of days: click the first, click the last.
 *
 * Weekends are not selectable. Range booking skips them, so letting
 * somebody start a range on a Saturday would promise a day that then
 * quietly disappears from the result.
 *
 * The office's booking window arrives as maxDate. Oslo allows two
 * working days, so most of the month is greyed out; Amsterdam has no
 * limit and passes null. The calendar does not know why -- it just
 * stops where it is told, and the database refuses anything that slips
 * through anyway.
 */
export function RangeCalendar({ from, to, onChange, minDate, maxDate }: Props) {
  const anchor = parse(from ?? minDate);
  const [view, setView] = useState({
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth(),
  });
  const [hover, setHover] = useState<string | null>(null);

  // A second click that lands before the first starts a new range
  // rather than selecting backwards, which is what people expect and
  // saves an explicit reset.
  const [pendingStart, setPendingStart] = useState<string | null>(null);

  const days = useMemo(() => monthGrid(view.year, view.month), [view]);

  const selectable = (day: Date): boolean => {
    const key = iso(day);
    if (key < minDate) return false;
    if (maxDate && key > maxDate) return false;
    const dow = day.getUTCDay();
    return dow !== 0 && dow !== 6;
  };

  const click = (day: Date) => {
    const key = iso(day);
    if (!selectable(day)) return;

    if (!pendingStart) {
      setPendingStart(key);
      onChange({ from: key, to: key });
      return;
    }
    if (key < pendingStart) {
      setPendingStart(key);
      onChange({ from: key, to: key });
      return;
    }
    onChange({ from: pendingStart, to: key });
    setPendingStart(null);
  };

  // While choosing the second end, the range under the cursor is shown
  // as if it were selected. Without it the first click looks like it
  // did nothing much.
  const previewEnd = pendingStart && hover && hover > pendingStart ? hover : null;
  const rangeStart = pendingStart ?? from;
  const rangeEnd = previewEnd ?? (pendingStart ? pendingStart : to);

  const inRange = (key: string) =>
    Boolean(rangeStart && rangeEnd && key >= rangeStart && key <= rangeEnd);

  const shift = (by: number) => {
    const next = new Date(Date.UTC(view.year, view.month + by, 1));
    setView({ year: next.getUTCFullYear(), month: next.getUTCMonth() });
  };

  // A month with nothing selectable in it is not worth navigating to.
  const monthStart = iso(new Date(Date.UTC(view.year, view.month, 1)));
  const monthEnd = iso(new Date(Date.UTC(view.year, view.month + 1, 0)));
  const canGoBack = monthStart > minDate;
  const canGoForward = !maxDate || monthEnd < maxDate;

  return (
    <div className="card" style={{ padding: 12 }}>
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}
      >
        <button className="quiet" onClick={() => shift(-1)} disabled={!canGoBack}
                aria-label="Previous month">
          ‹
        </button>
        <strong style={{ fontSize: 15 }}>
          {MONTH_NAMES[view.month]} {view.year}
        </strong>
        <button className="quiet" onClick={() => shift(1)} disabled={!canGoForward}
                aria-label="Next month">
          ›
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {DAY_NAMES.map((name) => (
          <div key={name} className="muted"
               style={{ textAlign: 'center', fontSize: 11, paddingBottom: 4 }}>
            {name}
          </div>
        ))}

        {days.map((day) => {
          const key = iso(day);
          const thisMonth = day.getUTCMonth() === view.month;
          const usable = selectable(day);
          const picked = inRange(key);
          const isEnd = key === rangeStart || key === rangeEnd;

          return (
            <button
              key={key}
              onClick={() => click(day)}
              onMouseEnter={() => setHover(key)}
              onMouseLeave={() => setHover(null)}
              disabled={!usable}
              aria-pressed={picked}
              aria-label={key}
              style={{
                padding: '7px 0',
                fontSize: 13,
                border: 0,
                borderRadius: isEnd ? 6 : 0,
                cursor: usable ? 'pointer' : 'default',
                background: picked
                  ? (isEnd ? 'var(--brand, #4c209e)' : 'rgba(76, 32, 158, 0.12)')
                  : 'transparent',
                color: picked && isEnd
                  ? '#fff'
                  : usable
                    ? (thisMonth ? 'inherit' : 'var(--ink-3, #7b8a93)')
                    : 'var(--ink-3, #7b8a93)',
                opacity: usable ? 1 : 0.4,
                fontWeight: isEnd ? 600 : 400,
              }}
            >
              {day.getUTCDate()}
            </button>
          );
        })}
      </div>

      <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
        {pendingStart
          ? 'Now pick the last day.'
          : from && to && from !== to
            ? 'Pick a day to start a new range.'
            : 'Pick the first day, then the last.'}
      </p>
    </div>
  );
}
